import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { crmService, CRMContact, CRMContactCreationRequest } from '../services/crmService';

const ContactsPage: React.FC = () => {
  const theme = useTheme();
  const [contacts, setContacts] = useState<CRMContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page] = useState(0);
  const [rowsPerPage] = useState(25);

  // Create contact dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CRMContactCreationRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    industry: '',
    location: '',
    notes: '',
  });

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<CRMContact | null>(null);
  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: '',
  });

  // Invoice dialog
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    description: '',
    amount: '',
    dueDate: '',
    notes: '',
  });

  // Filters
  const [industryFilter, setIndustryFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // Notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Load contacts
  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await crmService.getContacts({
        industry: industryFilter || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      // Filter by search on client side if needed
      let filteredContacts = response.contacts;
      if (searchFilter) {
        const searchLower = searchFilter.toLowerCase();
        filteredContacts = response.contacts.filter(contact =>
          contact.firstName?.toLowerCase().includes(searchLower) ||
          contact.lastName?.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.company?.toLowerCase().includes(searchLower)
        );
      }
      setContacts(filteredContacts);
      setTotal(searchFilter ? filteredContacts.length : response.total);
    } catch (error) {
      showSnackbar('Failed to load contacts', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Create contact
  const handleCreateContact = async () => {
    try {
      await crmService.createContact(createForm);
      showSnackbar('Contact created successfully!', 'success');
      setCreateDialogOpen(false);

      // Reset form
      setCreateForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        title: '',
        industry: '',
        location: '',
        notes: '',
      });

      // Reload contacts
      loadContacts();
    } catch (error) {
      showSnackbar('Failed to create contact', 'error');
    }
  };

  // Open email dialog
  const handleOpenEmailDialog = (contact: CRMContact) => {
    setSelectedContact(contact);
    setEmailForm({
      subject: `Hello ${contact.firstName}`,
      body: `Hi ${contact.firstName},\n\nI wanted to reach out regarding...`,
    });
    setEmailDialogOpen(true);
  };

  // Send email
  const handleSendEmail = async () => {
    if (!selectedContact) return;

    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch('http://localhost:3004/api/outreach/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          contactIds: [selectedContact.id],
          subject: emailForm.subject,
          body: emailForm.body,
        }),
      });

      if (!response.ok) throw new Error('Failed to send email');

      showSnackbar('Email sent successfully!', 'success');
      setEmailDialogOpen(false);
      setEmailForm({ subject: '', body: '' });
    } catch (error) {
      showSnackbar('Failed to send email', 'error');
    }
  };

  // Open invoice dialog
  const handleOpenInvoiceDialog = (contact: CRMContact) => {
    setSelectedContact(contact);
    setInvoiceForm({
      description: 'Services Rendered',
      amount: '',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      notes: '',
    });
    setInvoiceDialogOpen(true);
  };

  // Send invoice
  const handleSendInvoice = async () => {
    if (!selectedContact) return;

    try {
      const token = localStorage.getItem('redex_admin_token');
      const response = await fetch('http://localhost:3004/api/outreach/invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          contactId: selectedContact.id,
          items: [
            {
              description: invoiceForm.description,
              amount: Math.round(parseFloat(invoiceForm.amount) * 100), // Convert to cents
              quantity: 1,
            },
          ],
          dueDate: new Date(invoiceForm.dueDate).toISOString(),
          notes: invoiceForm.notes,
        }),
      });

      if (!response.ok) throw new Error('Failed to create invoice');

      const result = await response.json();
      showSnackbar('Invoice created and sent successfully!', 'success');
      setInvoiceDialogOpen(false);
      setInvoiceForm({ description: '', amount: '', dueDate: '', notes: '' });
    } catch (error) {
      showSnackbar('Failed to create invoice', 'error');
    }
  };

  // Sync data
  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await crmService.syncContactsToClients(true); // Dry run first
      showSnackbar(`Sync preview: ${result.syncedRecords} contacts ready to sync`, 'info');
    } catch (error) {
      showSnackbar('Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // Utility functions
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Effects
  useEffect(() => {
    loadContacts();
  }, [page, industryFilter, searchFilter]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: '1400px', mx: 'auto', py: 4, px: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
              CRM Contacts Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Manage and organize your business contacts
            </Typography>
          </Box>
        </Box>

      {/* Action Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Add Contact
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadContacts}
          disabled={loading}
          sx={{
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          Refresh
        </Button>

        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={20} sx={{ color: 'text.primary' }} /> : <SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
          sx={{
            color: 'text.primary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          Sync Data
        </Button>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search contacts..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{
            minWidth: 200,
            '& .MuiInputBase-root': { color: 'text.primary' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
          }}
        />

        {/* Filters */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ color: 'text.secondary' }}>Industry</InputLabel>
          <Select
            value={industryFilter}
            label="Industry"
            onChange={(e) => setIndustryFilter(e.target.value)}
            sx={{
              color: 'text.primary',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              '& .MuiSvgIcon-root': { color: 'text.primary' },
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Technology">Technology</MenuItem>
            <MenuItem value="Healthcare">Healthcare</MenuItem>
            <MenuItem value="Finance">Finance</MenuItem>
            <MenuItem value="Manufacturing">Manufacturing</MenuItem>
            <MenuItem value="Retail">Retail</MenuItem>
            <MenuItem value="Construction">Construction</MenuItem>
            <MenuItem value="Real Estate">Real Estate</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Total Contacts
              </Typography>
              <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 700 }}>
                {loading ? <CircularProgress size={24} sx={{ color: 'success.main' }} /> : total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Active Contacts
              </Typography>
              <Typography variant="h3" sx={{ color: 'info.main', fontWeight: 700 }}>
                {loading ? <CircularProgress size={24} sx={{ color: 'info.main' }} /> : (contacts || []).filter(c => c.isActive).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                With Email
              </Typography>
              <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700 }}>
                {loading ? <CircularProgress size={24} sx={{ color: 'primary.main' }} /> : (contacts || []).filter(c => c.email).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                With Phone
              </Typography>
              <Typography variant="h3" sx={{ color: 'success.main', fontWeight: 700 }}>
                {loading ? <CircularProgress size={24} sx={{ color: 'success.main' }} /> : (contacts || []).filter(c => c.phone).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contacts Table */}
      <Paper
        sx={{
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Company</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Contact Info</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Industry</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                  </TableCell>
                </TableRow>
              ) : !contacts || contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No contacts found. Try adding some contacts!
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    sx={{
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 600 }}>
                          {getInitials(contact.firstName, contact.lastName)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {contact.firstName} {contact.lastName}
                          </Typography>
                          {contact.email && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {contact.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                        <Typography sx={{ color: 'text.secondary' }}>
                          {contact.company || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {contact.title || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {contact.email && (
                          <Tooltip title={contact.email}>
                            <IconButton
                              size="small"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                              }}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {contact.phone && (
                          <Tooltip title={contact.phone}>
                            <IconButton
                              size="small"
                              sx={{
                                color: 'info.main',
                                '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) },
                              }}
                            >
                              <PhoneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {contact.industry || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {contact.location || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contact.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: contact.isActive ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                          color: contact.isActive ? 'success.main' : 'text.disabled',
                          border: `1px solid ${contact.isActive ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.grey[500], 0.3)}`,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </Box>

      {/* Create Contact Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
          Add New Contact
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Company"
                  value={createForm.company}
                  onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Title"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'text.secondary' }}>Industry</InputLabel>
                  <Select
                    value={createForm.industry}
                    label="Industry"
                    onChange={(e) => setCreateForm({ ...createForm, industry: e.target.value })}
                    sx={{
                      color: 'text.primary',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                      '& .MuiSvgIcon-root': { color: 'text.primary' },
                    }}
                  >
                    <MenuItem value="Technology">Technology</MenuItem>
                    <MenuItem value="Healthcare">Healthcare</MenuItem>
                    <MenuItem value="Finance">Finance</MenuItem>
                    <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                    <MenuItem value="Retail">Retail</MenuItem>
                    <MenuItem value="Construction">Construction</MenuItem>
                    <MenuItem value="Real Estate">Real Estate</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Location"
                  value={createForm.location}
                  onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  placeholder="City, State"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: 'text.secondary' },
                    '& .MuiInputBase-root': { color: 'text.primary' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              value={createForm.notes}
              onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateContact}
            variant="contained"
            disabled={!createForm.firstName.trim() || !createForm.lastName.trim() || !createForm.email.trim()}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Create Contact
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
          Send Email to {selectedContact?.firstName} {selectedContact?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Subject"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
            <TextField
              label="Message"
              value={emailForm.body}
              onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
              multiline
              rows={8}
              fullWidth
              required
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
          <Button onClick={() => setEmailDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!emailForm.subject.trim() || !emailForm.body.trim()}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={() => setInvoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
          Create Invoice for {selectedContact?.firstName} {selectedContact?.lastName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Description"
              value={invoiceForm.description}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
              fullWidth
              required
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
            <TextField
              label="Amount (USD)"
              type="number"
              value={invoiceForm.amount}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
            <TextField
              label="Due Date"
              type="date"
              value={invoiceForm.dueDate}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, dueDate: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
            <TextField
              label="Notes (optional)"
              value={invoiceForm.notes}
              onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
              multiline
              rows={3}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputBase-root': { color: 'text.primary' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
          <Button onClick={() => setInvoiceDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSendInvoice}
            variant="contained"
            startIcon={<ReceiptIcon />}
            disabled={!invoiceForm.description.trim() || !invoiceForm.amount || !invoiceForm.dueDate}
            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
          >
            Create & Send Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            bgcolor: snackbar.severity === 'error' ? alpha(theme.palette.error.main, 0.9) :
                    snackbar.severity === 'success' ? alpha(theme.palette.success.main, 0.9) :
                    alpha(theme.palette.info.main, 0.9),
            color: 'common.white',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactsPage;